import React, { useRef } from 'react';
import { QRCodeSVG } from 'qrcode.react';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Download, Car, User, Share2, ArrowLeft } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

const QRCodesPage = () => {
  const navigate = useNavigate();
  const userQRRef = useRef(null);
  const driverQRRef = useRef(null);
  const driverSignupQRRef = useRef(null);

  // Get the base URL (will be production URL after deployment)
  const baseUrl = window.location.origin;

  const qrCodes = [
    {
      id: 'user',
      title: 'Rider Portal',
      subtitle: 'For customers to book rides',
      url: `${baseUrl}/user`,
      ref: userQRRef,
      icon: User,
      color: 'from-blue-600 to-blue-700',
      bgColor: 'bg-blue-50',
      borderColor: 'border-blue-200',
    },
    {
      id: 'driver',
      title: 'Driver Portal',
      subtitle: 'For drivers to login & accept rides',
      url: `${baseUrl}/driver`,
      ref: driverQRRef,
      icon: Car,
      color: 'from-amber-500 to-yellow-500',
      bgColor: 'bg-amber-50',
      borderColor: 'border-amber-200',
    },
    {
      id: 'driver-signup',
      title: 'Driver Signup',
      subtitle: 'Recruit new drivers',
      url: `${baseUrl}/driver/signup`,
      ref: driverSignupQRRef,
      icon: Share2,
      color: 'from-green-600 to-emerald-600',
      bgColor: 'bg-green-50',
      borderColor: 'border-green-200',
    },
  ];

  const downloadQRCode = (qrRef, filename, title) => {
    const svg = qrRef.current.querySelector('svg');
    if (!svg) return;

    // Create a canvas with padding for the title
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    
    // QR code size + padding + title space
    const qrSize = 400;
    const padding = 40;
    const titleHeight = 80;
    const subtitleHeight = 30;
    const urlHeight = 40;
    const bottomPadding = 30;
    
    canvas.width = qrSize + (padding * 2);
    canvas.height = qrSize + (padding * 2) + titleHeight + subtitleHeight + urlHeight + bottomPadding;

    // White background
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, canvas.width, canvas.height);

    // Add J-Style Services branding
    ctx.fillStyle = '#1e3a5f';
    ctx.font = 'bold 28px Arial, sans-serif';
    ctx.textAlign = 'center';
    ctx.fillText('J-Style Services', canvas.width / 2, 45);

    // Add title
    ctx.fillStyle = '#333333';
    ctx.font = 'bold 22px Arial, sans-serif';
    ctx.fillText(title, canvas.width / 2, 85);

    // Convert SVG to image
    const svgData = new XMLSerializer().serializeToString(svg);
    const svgBlob = new Blob([svgData], { type: 'image/svg+xml;charset=utf-8' });
    const svgUrl = URL.createObjectURL(svgBlob);

    const img = new Image();
    img.onload = () => {
      // Draw QR code centered
      const qrX = padding;
      const qrY = titleHeight + subtitleHeight;
      ctx.drawImage(img, qrX, qrY, qrSize, qrSize);

      // Add URL below QR code
      ctx.fillStyle = '#666666';
      ctx.font = '14px Arial, sans-serif';
      const displayUrl = qrCodes.find(qr => qr.title === title)?.url || '';
      ctx.fillText(displayUrl, canvas.width / 2, qrY + qrSize + 25);

      // Add "Scan to access" text
      ctx.fillStyle = '#c9a227';
      ctx.font = 'bold 16px Arial, sans-serif';
      ctx.fillText('Scan to access', canvas.width / 2, qrY + qrSize + 50);

      // Download
      const link = document.createElement('a');
      link.download = `${filename}-qr-code.png`;
      link.href = canvas.toDataURL('image/png');
      link.click();

      URL.revokeObjectURL(svgUrl);
    };
    img.src = svgUrl;
  };

  const downloadAllQRCodes = () => {
    qrCodes.forEach((qr, index) => {
      setTimeout(() => {
        downloadQRCode(qr.ref, qr.id, qr.title);
      }, index * 500); // Stagger downloads
    });
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900">
      {/* Header */}
      <header className="bg-slate-900/80 backdrop-blur-sm border-b border-slate-700 sticky top-0 z-10">
        <div className="max-w-6xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Button 
                variant="ghost" 
                size="sm" 
                onClick={() => navigate('/links')}
                className="text-slate-300 hover:text-white hover:bg-slate-700"
              >
                <ArrowLeft size={18} className="mr-1" />
                Back
              </Button>
              <div>
                <h1 className="text-2xl font-bold text-white">QR Codes</h1>
                <p className="text-slate-400 text-sm">Download for flyers & social media</p>
              </div>
            </div>
            <Button
              onClick={downloadAllQRCodes}
              className="bg-gradient-to-r from-amber-500 to-yellow-500 hover:from-amber-600 hover:to-yellow-600 text-slate-900 font-semibold"
            >
              <Download size={18} className="mr-2" />
              Download All
            </Button>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-6xl mx-auto px-4 py-8">
        {/* Info Banner */}
        <Card className="mb-8 p-4 bg-blue-900/30 border-blue-700/50">
          <p className="text-blue-200 text-sm text-center">
            📱 These QR codes will automatically use your <strong>production URL</strong> after deployment. 
            Perfect for business cards, flyers, vehicle stickers, and social media!
          </p>
        </Card>

        {/* QR Code Grid */}
        <div className="grid md:grid-cols-3 gap-6">
          {qrCodes.map((qr) => {
            const Icon = qr.icon;
            return (
              <Card 
                key={qr.id} 
                className={`p-6 ${qr.bgColor} ${qr.borderColor} border-2`}
                data-testid={`qr-card-${qr.id}`}
              >
                {/* Header */}
                <div className="flex items-center gap-3 mb-4">
                  <div className={`w-12 h-12 rounded-full bg-gradient-to-r ${qr.color} flex items-center justify-center`}>
                    <Icon className="text-white" size={24} />
                  </div>
                  <div>
                    <h3 className="font-bold text-lg text-slate-800">{qr.title}</h3>
                    <p className="text-sm text-slate-600">{qr.subtitle}</p>
                  </div>
                </div>

                {/* QR Code */}
                <div 
                  ref={qr.ref}
                  className="bg-white p-6 rounded-xl shadow-inner flex items-center justify-center mb-4"
                >
                  <QRCodeSVG
                    value={qr.url}
                    size={200}
                    level="H"
                    includeMargin={true}
                    fgColor="#1e3a5f"
                    imageSettings={{
                      src: "",
                      height: 0,
                      width: 0,
                      excavate: false,
                    }}
                  />
                </div>

                {/* URL Display */}
                <div className="bg-white/50 rounded-lg p-3 mb-4">
                  <p className="text-xs text-slate-500 text-center break-all font-mono">
                    {qr.url}
                  </p>
                </div>

                {/* Download Button */}
                <Button
                  onClick={() => downloadQRCode(qr.ref, qr.id, qr.title)}
                  className={`w-full bg-gradient-to-r ${qr.color} hover:opacity-90 text-white font-semibold`}
                  data-testid={`download-qr-${qr.id}`}
                >
                  <Download size={18} className="mr-2" />
                  Download PNG
                </Button>
              </Card>
            );
          })}
        </div>

        {/* Usage Tips */}
        <Card className="mt-8 p-6 bg-slate-800/50 border-slate-700">
          <h3 className="text-lg font-bold text-white mb-4 flex items-center gap-2">
            <Share2 size={20} className="text-amber-400" />
            Usage Ideas
          </h3>
          <div className="grid md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <h4 className="font-semibold text-amber-400">For Riders</h4>
              <ul className="text-slate-300 text-sm space-y-1">
                <li>• Business cards & flyers</li>
                <li>• Social media posts</li>
                <li>• Restaurant & hotel partnerships</li>
                <li>• Event promotions</li>
              </ul>
            </div>
            <div className="space-y-2">
              <h4 className="font-semibold text-amber-400">For Drivers</h4>
              <ul className="text-slate-300 text-sm space-y-1">
                <li>• Recruitment flyers</li>
                <li>• Vehicle window stickers</li>
                <li>• Job fair materials</li>
                <li>• Referral cards</li>
              </ul>
            </div>
          </div>
        </Card>

        {/* Print Tips */}
        <Card className="mt-4 p-4 bg-green-900/30 border-green-700/50">
          <p className="text-green-200 text-sm text-center">
            💡 <strong>Tip:</strong> Downloaded images are high-resolution (400x400px QR code) and ready for printing up to 4 inches without quality loss.
          </p>
        </Card>
      </main>
    </div>
  );
};

export default QRCodesPage;
